import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, ShieldCheck, WarningCircle } from "@phosphor-icons/react";

export default function Profile() {
  const { user } = useAuth();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false);

  const isGoogle = (user?.auth_provider || "password") === "google";

  const submit = async (e) => {
    e.preventDefault();
    if (!cur || !nw || !cf) return toast.error("Please fill all fields");
    if (nw.length < 6) return toast.error("New password must be at least 6 characters");
    if (nw !== cf) return toast.error("New password and confirm password do not match");
    if (nw === cur) return toast.error("New password must be different from current password");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: cur,
        new_password: nw,
      });
      toast.success("Password updated. Use your new password from next login.");
      setCur(""); setNw(""); setCf("");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl">
      <div className="mb-6">
        <h1 className="text-3xl font-display font-bold flex items-center gap-2">
          <Lock size={26} weight="duotone" /> Change Password
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Update your account password. You'll stay logged in on this device.
        </p>
      </div>

      <Card className="p-6">
        <div className="mb-4 pb-4 border-b border-border">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Signed in as</div>
          <div className="font-semibold text-base">{user?.email}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Auth: <span className="font-mono">{user?.auth_provider || "password"}</span>
          </div>
        </div>

        {isGoogle ? (
          <div className="p-4 rounded border border-amber-300 bg-amber-50 flex gap-3 text-sm">
            <WarningCircle size={22} className="text-amber-600 shrink-0" />
            <div>
              <div className="font-semibold text-amber-900">Password not available</div>
              <div className="text-amber-800 mt-1">
                You signed in with Google. Change your password from your{" "}
                <a
                  href="https://myaccount.google.com/security"
                  target="_blank"
                  rel="noreferrer"
                  className="underline font-medium"
                >
                  Google Account
                </a>{" "}
                settings instead.
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
            <div>
              <Label>Current Password</Label>
              <Input
                type="password" autoComplete="current-password"
                value={cur} onChange={(e) => setCur(e.target.value)}
                data-testid="current-password-input"
                placeholder="Enter your current password"
              />
            </div>
            <div>
              <Label>New Password (min 6 characters)</Label>
              <Input
                type="password" autoComplete="new-password"
                value={nw} onChange={(e) => setNw(e.target.value)}
                data-testid="new-password-input"
                placeholder="Choose a new password"
              />
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input
                type="password" autoComplete="new-password"
                value={cf} onChange={(e) => setCf(e.target.value)}
                data-testid="confirm-password-input"
                placeholder="Re-enter new password"
              />
              {cf && nw && cf !== nw && (
                <div className="text-xs text-destructive mt-1">Passwords do not match</div>
              )}
            </div>

            <div className="pt-2 flex items-center gap-2">
              <Button
                type="submit"
                disabled={busy}
                className="bg-primary text-primary-foreground"
                data-testid="change-password-submit"
              >
                <ShieldCheck size={16} className="mr-1" />
                {busy ? "Updating…" : "Update Password"}
              </Button>
              <span className="text-xs text-muted-foreground">
                Stored securely with bcrypt hashing
              </span>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
